<template>
  <form
    data-testid="login-form"
    class="login-form landing-form-cb"
    @submit.prevent="submitLogin"
  >
    <label class="reg-label mb-1">Willkommen zurück!</label>
    <div class="login-flex">
      <div class="login-left">
        <div class="form-group mb-2">
          <input
            v-model="email"
            type="email"
            data-testid="login-email"
            name="login-email"
            required
            :placeholder="$t('login.web.email_placeholder')"
            class="form-control"
            :autofocus="true"
            autocomplete="email"
          />
        </div>

        <div class="form-group mb-0">
          <input
            v-model="password"
            type="password"
            data-testid="login-password"
            name="password"
            required
            placeholder="*******"
            class="form-control"
            autocomplete="current-password"
          />
        </div>

        <p class="text-end small mb-1">
          <a
            href="#"
            data-testid="forgot-password-link"
            class="form-link-cb"
            @click.prevent="handlePasswordForget"
            >{{ $t("login.password_forgotten") }}</a
          >
        </p>

        <div class="d-flex align-items-center justify-content-center">
          <button
            type="submit"
            name="login-submit"
            class="cb-submit-button w-sm-50"
          >
            {{ $t("login.web.login") }}
          </button>
        </div>

        <div class="d-flex justify-content-center mt-3">
          <div class="form-group mb-0">
            <label
              tabindex="0"
              role="checkbox"
              class="modern-check-label"
              for="stay-logged-in-check"
            >
              <div class="pseudo-label text-uppercase">
                {{ $t("login.web.stay_logged_in") }}
              </div>
              <input
                id="stay-logged-in-check"
                v-model="stayLoggedInCheck"
                data-testid="stay-logged-in-check"
                type="checkbox"
                tabindex="-1"
                @change="$emit('stay', stayLoggedInCheck)"
              />
              <span class="blue-checkmark"></span>
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="
          $whitelabel.featuresEnabled.paypal_login ||
          $whitelabel.featuresEnabled.google ||
          $whitelabel.featuresEnabled.facebook ||
          $whitelabel.featuresEnabled.apple
        "
        class="login-right"
      >
        <p class="medium toggle-text social-title mb-4">
          {{ $t("login.3rd_party_login") }}
        </p>

        <div class="social-grid">
          <button
            v-if="$whitelabel.featuresEnabled.apple"
            type="button"
            class="social-btn"
          >
            <img src="@/assets/img/social/apple.png" alt="Apple" />
          </button>
          <div
            v-if="
              $store.getters.facebookEnabled &&
              $whitelabel.featuresEnabled.facebook
            "
            class="social-btn fb-wrapper"
          >
            <v-facebook-login
              :app-id="facebookAppId"
              type="button"
              version="v18.0"
              class="vfb-as-icon"
              :class="{ 'd-none': !displayFbButton }"
              @login="handleFbLogin"
              @logout="$emit('facebookLogout')"
              @sdk-init="displayFbButton = true"
            >
              <template #login>
                <img src="@/assets/img/social/facebook.png" alt="Facebook" />
              </template>
            </v-facebook-login>
          </div>

          <button
            v-if="$whitelabel.featuresEnabled.google"
            type="button"
            class="social-btn"
            @click="handleGoogleLogin"
          >
            <img src="@/assets/img/social/google.png" alt="Google" />
          </button>

          <button
            v-if="$whitelabel.featuresEnabled.paypal_login"
            class="social-btn"
            @click="startPaypalLogin"
          >
            <img
              src="@/assets/img/social/paypal.png"
              alt="Log in with PayPal"
            />
          </button>
        </div>
      </div>
      <p data-testid="toggle-text" class="medium toggle-text mt-4">
        <i18next
          :translation="$t('login.web.register.register_message')"
          @click="$emit('toggle')"
        >
          <template #link>
            <a href="#" @click.prevent="$emit('toggle')">{{
              $t("login.web.register.here_link")
            }}</a></template
          >
        </i18next>
      </p>
    </div>

    <modal-password-forget
      :reset-email="resetEmail"
      @confirm-email="handleConfirm"
    ></modal-password-forget>
  </form>
</template>

<script>
import { VFBLogin as VFacebookLogin } from "vue-facebook-login-component";
import ModalPasswordForget from "@/components/landing/modal-password-forget.vue";

export default {
  name: "Login",
  components: { VFacebookLogin, ModalPasswordForget },
  props: {
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      email: "",
      resetEmail: "",
      password: "",
      stayLoggedInCheck: true,
      facebookAppId: process.env.VUE_APP_FACEBOOK_APP_ID,
      displayFbButton: false,
    };
  },
  watch: {
    error() {
      if (this.error) {
        this.password = "";
      }
    },
  },
  mounted() {
    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {
      if (modalId === "password-forget") {
        this.resetEmail = this.email;
      }
    });
    this.$store.commit("setIsPageLoading", false);
  },

  methods: {
    startPaypalLogin() {
      const base =
        process.env.NODE_ENV === "production"
          ? "https://www.paypal.com/signin/authorize"
          : "https://www.sandbox.paypal.com/signin/authorize";

      const clientId = process.env.VUE_APP_PAYPAL_CLIENT_ID;

      const redirectUri = `${window.location.origin}/login`;
      const state = encodeURIComponent(window.location.href);

      const authUrl = new URL(base);
      authUrl.searchParams.set("flowEntry", "static");
      authUrl.searchParams.set("client_id", clientId);
      authUrl.searchParams.set("response_type", "code");
      authUrl.searchParams.set("scope", "openid profile email");
      authUrl.searchParams.set("redirect_uri", redirectUri);
      authUrl.searchParams.set("state", state);

      window.location.assign(authUrl.toString());
    },
    async handleGoogleLogin() {
      try {
        const provider = new firebase.auth.GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        const result = await firebase.auth().signInWithPopup(provider);
        const accessToken = await result.credential.accessToken;
        this.$emit("googleConnected", accessToken);
      } catch (e) {
        console.log("Google sign-in failed:", e);
        this.$emit("error");
      }
    },
    submitLogin: function () {
      let email = this.email;
      let password = this.password;
      this.$emit("submitLogin", email, password);
    },
    toggleStayLoggedIn() {
      this.stayLoggedInCheck = !this.stayLoggedInCheck;
    },
    handlePasswordForget() {
      this.$bvModal.show("password-forget");
    },
    handleFbLogin(e) {
      if (e.status === "connected") {
        this.$emit("facebookConnected", e.authResponse.accessToken);
      } else {
        console.log(e);
      }
    },
    handleConfirm(data) {
      this.resetEmail = data;

      if (this.resetEmail.length > 8) {
        this.$emit("handleResetRequest", this.resetEmail);
      }
      this.$bvModal.hide("password-forget");
    },
  },
};
</script>
<style lang="scss">
.login-form {
  margin-top: 4rem;
}
.input-window {
  .modal-header {
    border: none !important;
    flex-wrap: wrap;
    justify-content: center;
    .close-button {
      right: -16px;
      top: -16px;
      width: 32px;
      height: 32px;
    }
  }
  .modal-content {
    color: white;
    text-align: center;
    background-color: transparent;
    width: 100%;
    background-image: linear-gradient(
        $bg-gradient-deep-ocean,
        $bg-gradient-navy-blue
      ),
      linear-gradient($bg-gradient-navy-blue, $bg-gradient-deep-ocean);
    border: double 1px transparent;
    background-origin: border-box;
    background-clip: padding-box, border-box;
    background-size: 100% 100%;
    min-height: 128px;
  }
  .modal-body {
    padding: 0;
  }
  .modal-footer {
    border-top: 0;
    justify-content: center;
  }

  .modal-buttons {
    justify-content: space-around;
    width: 80%;
  }
}
.login-flex {
  display: flex;
  flex-direction: column;

  .login-left {
    display: flex;
    flex-direction: column;
    min-width: unset;
    max-width: 100%;
  }

  .login-right {
    flex: unset;
    min-width: unset;
    max-width: 100%;

    .social-title {
      font-weight: 600;
      text-align: center;
      margin-bottom: 0.5rem;
    }

    .social-grid {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: 0.75rem;
      max-width: 100%;
      margin: 0 auto;
    }

    .social-grid .v-facebook-login {
      background: transparent !important;
      padding: 0 !important;
      border: 0 !important;
      width: 50px !important;
      height: 50px !important;
      min-width: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 0 !important;
    }

    .social-grid .v-facebook-login > svg.token,
    .social-grid .v-facebook-login > svg {
      display: none !important;
    }

    .social-grid .v-facebook-login img {
      width: 50px !important;
      height: 50px !important;
      object-fit: contain !important;
      display: block !important;
      filter: drop-shadow(0 0 8px rgba(233, 76, 255, 0.6));
    }

    .social-btn {
      background: transparent;
      border: none;
      border-radius: 8px;
      width: 100px;
      height: 40px;
      padding: 0;
      cursor: pointer;

      display: flex;
      align-items: center;
      justify-content: center;

      transition: all 0.2s ease;

      img {
        width: 80px;
        height: 80px;
        object-fit: contain;
        filter: drop-shadow(0 0 4px rgba(233, 76, 255, 0.4));
      }
    }
    .fb-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;

      .v-facebook-login {
        all: unset;
        width: 100%;
        height: 100%;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        button {
          all: unset !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100%;
          height: 100%;
          margin: 0 !important;
          padding: 0 !important;
          line-height: normal !important;
        }

        img {
          width: 80px !important;
          height: 80px !important;
          display: block !important;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .login-flex {
      flex-direction: column;
    }
    .login-right {
      width: 100%;
    }
  }
}
</style>
